import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  Stack
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Update as UpdateIcon,
  CheckCircle as CheckCircleIcon,
  ShoppingCart as ShoppingCartIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getData } from "../../Webservice/webservice";

const Notification = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(20);

  useEffect(
    () => {
      fetchNotifications();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [page]
  );

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      const response = await getData(
        "vendor/notification/vend",
        { page, limit },
        token
      );

      if (response && response.success) {
        setNotifications(response.data || []);
        if (response.meta) {
          setTotalPages(response.meta.totalPages || 1);
        }
      } else {
        setError("Failed to load notifications.");
      }
    } catch (err) {
      console.error("Fetch notifications error:", err);
      setError("An error occurred while fetching notifications.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getIcon = type => {
    switch (type) {
      case "vendor_order":
        return <ShoppingCartIcon color="primary" />;
      case "vendor_request":
        return <UpdateIcon color="primary" />;
      case "order_update":
        return <CheckCircleIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const handleNotificationClick = notif => {
    if (notif.type === "vendor_order") {
      navigate("/orders");
    }
    // Add logic here for other linkable types if needed
  };

  const formatTime = dateString => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, margin: "0 auto" }}>
      {/* Header & Breadcrumbs */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/")}
            sx={{ cursor: "pointer", fontFamily: "Mulish, sans-serif" }}
          >
            Dashboard
          </Link>
          <Typography
            color="text.primary"
            sx={{ fontFamily: "Mulish, sans-serif" }}
          >
            Notifications
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontFamily: "Mulish, sans-serif",
              color: "#1e293b"
            }}
          >
            Notifications
          </Typography>
        </Box>
      </Box>

      {/* Notifications List */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          //   minHeight: 200,
          display: "flex",
          flexDirection: "column",
          mb: 4
        }}
      >
        {loading
          ? <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 10,
                flexGrow: 1
              }}
            >
              <CircularProgress />
            </Box>
          : error
            ? <Box sx={{ p: 4, flexGrow: 1 }}>
                <Alert severity="error">
                  {error}
                </Alert>
              </Box>
            : notifications.length > 0
              ? <List sx={{ p: 0 }}>
                  {notifications.map((notif, index) =>
                    <React.Fragment key={notif._id}>
                      <ListItem
                        alignItems="flex-start"
                        onClick={() => handleNotificationClick(notif)}
                        sx={{
                          py: 1.5,
                          px: 3,
                          cursor: "pointer",
                          bgcolor: notif.isRead
                            ? "transparent"
                            : "rgba(99, 102, 241, 0.04)",
                          transition: "background-color 0.2s",
                          "&:hover": {
                            bgcolor: "rgba(0, 0, 0, 0.02)"
                          }
                        }}
                      >
                        <ListItemIcon sx={{ mt: 0.5, minWidth: 48 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "background.paper",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                              border: "1px solid #f1f5f9"
                            }}
                          >
                            {getIcon(notif.type)}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 0.5
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: "#334155",
                                  fontFamily: "Mulish, sans-serif"
                                }}
                              >
                                {notif.title}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#94a3b8", fontWeight: 500 }}
                              >
                                {formatTime(notif.createdAt)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#64748b",
                                fontFamily: "Mulish, sans-serif",
                                lineHeight: 1.6
                              }}
                            >
                              {notif.message}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < notifications.length - 1 &&
                        <Divider component="li" />}
                    </React.Fragment>
                  )}
                </List>
              : <Box
                  sx={{
                    p: 10,
                    textAlign: "center",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                >
                  <NotificationsIcon
                    sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ color: "#64748b", fontWeight: 600 }}
                  >
                    All caught up!
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    You don't have any new notifications.
                  </Typography>
                </Box>}
      </Paper>

      {/* Pagination */}
      {!loading &&
        !error &&
        notifications.length > 0 &&
        totalPages > 1 &&
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            mt: 2,
            mb: 6
          }}
        >
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: "100px",
              px: 1.5,
              py: 0.5,
              display: "inline-flex",
              alignItems: "center"
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              size="medium"
              sx={{
                "& .MuiPagination-ul": { gap: 0.5 },
                "& .MuiPaginationItem-root": {
                  fontFamily: "Mulish, sans-serif",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: "#64748b",
                  minWidth: 35,
                  height: 35,
                  borderRadius: "30%",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: "rgba(142, 158, 211, 0.1)",
                    color: "#8e9ed3"
                  },
                  "&.Mui-selected": {
                    bgcolor: "#474b55 !important",
                    color: "#ffffff",
                    boxShadow: "0 4px 12px rgba(142, 158, 211, 0.35)",
                    "&:hover": { bgcolor: "#474b55 !important" }
                  },
                  "&.MuiPaginationItem-previousNext": {
                    color: "#cbd5e1",
                    "& svg": { fontSize: "1.5rem" },
                    "&:hover": { bgcolor: "transparent", color: "#8e9ed3" }
                  },
                  "&.Mui-disabled": { opacity: 0.3 }
                }
              }}
            />
          </Box>
        </Box>}
    </Box>
  );
};

export default Notification;
